import React, { FC, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useRouter } from "next/router";

import useHiddenLocationGroupTypes from "@src/features/home/hooks/useHiddenLocationGroupTypes";
import {
  FolderLibraryItemFragment,
  useFoldersGrid_FoldersQuery,
} from "@src/features/library/all/FoldersGrid.generated";
import { Button } from "@src/lib/ui/Button";
import ProgramReportSidebar from "@src/features/reporting/programReport/ProgramReportSidebar";
import { usePortalTarget } from "@src/lib/hooks/usePortalTarget";
import { cn } from "@src/lib/utils/cn";
import BookOpenOutlineIcon from "@src/lib/icons/18px/book-open-outline";
import BookmarkOutlineIcon from "@src/lib/icons/18px/bookmark-outline";
import CircleCheckmarkOutlineIcon from "@src/lib/icons/18px/circle-checkmark-outline";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";
import FolderOpenOutlineIcon from "@src/lib/icons/18px/folder-open-outline";
import GridOutlineIcon from "@src/lib/icons/18px/grid-outline";
import SidebarLeftHideOutlineIcon from "@src/lib/icons/18px/sidebar-left-hide-outline";
import SidebarLeftShowOutlineIcon from "@src/lib/icons/18px/sidebar-left-show-outline";
import SuitcaseOutlineIcon from "@src/lib/icons/18px/suitcase-outline";
import MessagingActivityNavItem from "@src/features/messaging/new/activity/MessagingActivityNavItem";
import ChatsNavSection, {
  ChatsSearchHidden,
  ChatsSearchInput,
  ChatsSearchProvider,
} from "@src/features/messaging/new/chats/ChatsNavSection";
import RecommendationsSecondaryNavSection from "@src/features/recommendations/RecommendationsSecondaryNavSection";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff, LibraryItemType } from "@src/types.generated";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@src/lib/ui/Sidebar";
import Text from "@src/lib/ui/Text";

import {
  AdminNavVisibilityContext,
  AdminPrimaryNavItem,
  AdminSecondaryNavItem,
  getVisibleSecondaryItems,
} from "./adminNavConfig";
import { useIsAdminContentBuilderView } from "./useIsAdminContentBuilderView";

type Props = {
  activeItem: AdminPrimaryNavItem;
  visibilityContext: AdminNavVisibilityContext;
  collapsed?: boolean;
};

/** Align section title with the main shell top-nav page title (TopNav is h-11). */
const SECONDARY_NAV_HEADER_CLASS =
  "flex-row items-center justify-between gap-2 px-3 pt-2 pb-2";

const SECONDARY_NAV_MENU_BUTTON_CLASS = "text-gray-11 [&>svg]:size-4";

const SECONDARY_NAV_CONTAINER_CLASS =
  "hide-scrollbar flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto rounded-[10px] bg-transparent shadow-default md:my-2";

const AdminSecondaryNavMenu: FC<Props> = ({
  activeItem,
  visibilityContext,
  collapsed = false,
}) => {
  const { pathname, query } = useRouter();
  const { open, setOpen } = useSidebar();
  const isContentBuilderView = useIsAdminContentBuilderView() || collapsed;
  const leadingPortal = usePortalTarget("top-nav-leading");
  const hiddenLocationGroupTypes = useHiddenLocationGroupTypes();
  const scormEnabled = useFeatureFlag(Ff.ScormResources);
  const { data: foldersData } = useFoldersGrid_FoldersQuery({
    variables: {
      input: {
        filters: {
          folderId: { value: undefined },
          itemType: { value: LibraryItemType.Folder },
        },
      },
    },
    skip: activeItem.id !== "training",
  });
  const sortedFolders = useMemo(() => {
    const folders = foldersData?.AdminLibrary.objects ?? [];
    return [...folders].sort((a, b) =>
      (a.folder?.position || "").localeCompare(b.folder?.position || ""),
    );
  }, [foldersData]);
  const visibleItems = getVisibleSecondaryItems(
    activeItem.items,
    visibilityContext,
  );
  const railOffset = "var(--admin-primary-rail-collapsed-width)";
  // The menu is position:fixed while the page's left offset comes from an
  // in-flow spacer whose width animates over 200ms (AdminLeftNav). A menu
  // that paints at its resting position sits on top of the page content for
  // the whole animation — or for however long a busy main thread freezes the
  // frame mid-flight, which strands page content under the transparent menu
  // and eats its clicks (OPUS-18277). So the menu stays put and a clip-path
  // on the fixed aside itself uncovers it left-to-right, with the same 200ms
  // ease-out, as the page edge moves away: the clipped-away region is both
  // unpainted and — because clip-path drives hit-testing — click-through, so
  // clicks over the still-visible page land on the page, not the menu. The
  // clip is on the aside, not the inner container, precisely so the fixed
  // box stops catching those clicks. The -12px outsets keep the container's
  // shadow (~6px) unclipped, so the settled clip renders identically to no
  // clip. Reduced motion skips the reveal: the layout effect flushes the
  // settled state before first paint.
  const [revealed, setRevealed] = useState(false);
  useLayoutEffect(() => {
    if (!open) {
      setRevealed(false);
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setRevealed(true);
      return;
    }
    // Double rAF: the first paint must commit the covered state or there is
    // nothing for the clip-path transition to animate from.
    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setRevealed(true));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
    };
  }, [open]);
  // Below md the menu floats over the page instead of taking width from it, so
  // it needs its own ground.
  const asideClassName = cn(
    "fixed top-(--impersonation-banner-height) bottom-0 z-20 flex w-[var(--admin-secondary-menu-width)] flex-col transition-[clip-path] duration-200 ease-out motion-reduce:transition-none max-md:rounded-r-xl max-md:bg-bg-canvas max-md:shadow-lg",
  );
  const asideStyle: React.CSSProperties = {
    left: railOffset,
    // Covered: clip the whole width away from the right, so the menu's visible
    // right edge sits on the rail and grows rightward as the page edge advances
    // (left-to-right reveal). Settled: -12px all round leaves the ~6px shadow
    // unclipped, identical to no clip.
    clipPath: revealed
      ? "inset(-12px)"
      : "inset(-12px calc(100% + 12px) -12px -12px)",
  };

  // The secondary menu always opens by default whenever a section that has one
  // becomes active. This re-opens the menu on navigation between sections even
  // if the user explicitly closed it on the previous section. Using
  // `useLayoutEffect` flushes the state update before paint to avoid flashing
  // the closed-state "Show" button when a new section mounts.
  useLayoutEffect(() => {
    if (isContentBuilderView) {
      return;
    }
    if (!open) setOpen(true);
    // We intentionally only react to section changes here; we do not want to
    // override the user closing the menu on the current section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem.id]);

  if (!open) {
    if (!leadingPortal || isContentBuilderView) return null;

    return createPortal(
      <Button
        type="button"
        variant="ghost"
        size="icon-md"
        aria-label="Show secondary navigation"
        className="size-7 rounded-md text-gray-9 hover:!bg-black-alpha-2 hover:text-gray-12"
        onClick={() => setOpen(true)}
      >
        <SidebarLeftShowOutlineIcon className="size-4" />
      </Button>,
      leadingPortal,
    );
  }

  if (activeItem.id === "reporting") {
    return (
      <aside
        aria-label="Reporting navigation"
        className={asideClassName}
        style={asideStyle}
      >
        <div className={SECONDARY_NAV_CONTAINER_CLASS}>
          <SidebarHeader className={SECONDARY_NAV_HEADER_CLASS}>
            <Text variant="body-title" className="truncate text-gray-12">
              {activeItem.label}
            </Text>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Hide secondary navigation"
              className="shrink-0"
              onClick={() => setOpen(false)}
            >
              <SidebarLeftHideOutlineIcon className="size-4" />
            </Button>
          </SidebarHeader>

          <SidebarContent className="px-3 pb-3">
            <ProgramReportSidebar />
          </SidebarContent>
        </div>
      </aside>
    );
  }

  if (activeItem.id === "training") {
    return (
      <aside
        aria-label="Training navigation"
        className={asideClassName}
        style={asideStyle}
      >
        <div className={SECONDARY_NAV_CONTAINER_CLASS}>
          <SidebarHeader className={SECONDARY_NAV_HEADER_CLASS}>
            <Text variant="body-title" className="truncate text-gray-12">
              {activeItem.label}
            </Text>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Hide secondary navigation"
              className="shrink-0"
              onClick={() => setOpen(false)}
            >
              <SidebarLeftHideOutlineIcon className="size-4" />
            </Button>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <Text
                  variant="fine-print-title"
                  multiline
                  className="mb-1 px-2 text-gray-8"
                >
                  Library
                </Text>
                <SidebarMenu>
                  {LIBRARY_TYPE_NAV_ITEMS.map((item) => (
                    <LibraryTypeMenuItem
                      key={item.value}
                      item={item}
                      pathname={pathname}
                      type={query.type}
                    />
                  ))}
                  {scormEnabled && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={pathname === "/library/scorm-courses"}
                        aria-current={
                          pathname === "/library/scorm-courses"
                            ? "page"
                            : undefined
                        }
                        className={SECONDARY_NAV_MENU_BUTTON_CLASS}
                      >
                        <Link href="/library/scorm-courses">
                          <SuitcaseOutlineIcon />
                          <span>SCORM</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
                <SidebarMenu className="mt-4">
                  {visibleItems
                    .filter(
                      (item) =>
                        item.label !== "Library" && item.label !== "Archive",
                    )
                    .map((item) => (
                      <SecondaryMenuItem
                        key={item.href ?? item.label}
                        item={item}
                        pathname={pathname}
                        queryType={query.type}
                      />
                    ))}
                </SidebarMenu>
                {sortedFolders.length > 0 ? (
                  <>
                    <Text
                      variant="fine-print-title"
                      multiline
                      className="mt-4 mb-1 px-2 text-gray-8"
                    >
                      Folders
                    </Text>
                    <SidebarMenu>
                      {sortedFolders.map((folder) => (
                        <FolderMenuItem
                          key={folder.id}
                          folder={folder}
                          pathname={pathname}
                          activeId={query.id}
                        />
                      ))}
                    </SidebarMenu>
                  </>
                ) : null}
                {visibleItems
                  .filter((item) => item.label === "Archive")
                  .map((item) => (
                    <SidebarMenu key={item.href ?? item.label} className="mt-4">
                      <SecondaryMenuItem
                        item={item}
                        pathname={pathname}
                        queryType={query.type}
                      />
                    </SidebarMenu>
                  ))}
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </div>
      </aside>
    );
  }

  if (activeItem.id === "recommendations") {
    return (
      <aside
        aria-label="Recommendations navigation"
        className={asideClassName}
        style={asideStyle}
      >
        <div className={SECONDARY_NAV_CONTAINER_CLASS}>
          <SidebarHeader className={SECONDARY_NAV_HEADER_CLASS}>
            <Text variant="body-title" className="truncate text-gray-12">
              {activeItem.label}
            </Text>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Hide secondary navigation"
              className="shrink-0"
              onClick={() => setOpen(false)}
            >
              <SidebarLeftHideOutlineIcon className="size-4" />
            </Button>
          </SidebarHeader>

          <SidebarContent>
            <RecommendationsSecondaryNavSection />
          </SidebarContent>
        </div>
      </aside>
    );
  }

  if (activeItem.id === "people") {
    return (
      <aside
        aria-label="People navigation"
        className={asideClassName}
        style={asideStyle}
      >
        <div className={SECONDARY_NAV_CONTAINER_CLASS}>
          <SidebarHeader className={SECONDARY_NAV_HEADER_CLASS}>
            <Text variant="body-title" className="truncate text-gray-12">
              {activeItem.label}
            </Text>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Hide secondary navigation"
              className="shrink-0"
              onClick={() => setOpen(false)}
            >
              <SidebarLeftHideOutlineIcon className="size-4" />
            </Button>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems
                    .filter(
                      (item) =>
                        !item.locationGroupType ||
                        !hiddenLocationGroupTypes.includes(
                          item.locationGroupType,
                        ),
                    )
                    .map((item) => (
                      <SecondaryMenuItem
                        key={item.href ?? item.label}
                        item={item}
                        pathname={pathname}
                        queryType={query.type}
                      />
                    ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </div>
      </aside>
    );
  }

  const secondaryItemsNode = (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) =>
            activeItem.id === "newsfeed" &&
            item.href === "/newsfeed/activity" ? (
              <MessagingActivityNavItem
                key={item.href ?? item.label}
                active={item.isActive?.(pathname) ?? pathname === item.href}
              />
            ) : (
              <SecondaryMenuItem
                key={item.href ?? item.label}
                item={item}
                pathname={pathname}
                queryType={query.type}
              />
            ),
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <aside
      aria-label={`${activeItem.label} navigation`}
      className={asideClassName}
      style={asideStyle}
    >
      <div className={SECONDARY_NAV_CONTAINER_CLASS}>
        <SidebarHeader className={SECONDARY_NAV_HEADER_CLASS}>
          <Text variant="body-title" className="truncate text-gray-12">
            {activeItem.label}
          </Text>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Hide secondary navigation"
            className="shrink-0"
            onClick={() => setOpen(false)}
          >
            <SidebarLeftHideOutlineIcon className="size-4" />
          </Button>
        </SidebarHeader>

        <SidebarContent>
          {activeItem.id === "messages" ? (
            <ChatsSearchProvider>
              <SidebarGroup className="pb-1">
                <SidebarGroupContent>
                  <ChatsSearchInput />
                </SidebarGroupContent>
              </SidebarGroup>
              <ChatsSearchHidden>{secondaryItemsNode}</ChatsSearchHidden>
              <ChatsNavSection />
            </ChatsSearchProvider>
          ) : (
            secondaryItemsNode
          )}
        </SidebarContent>
      </div>
    </aside>
  );
};

type LibraryTypeNavValue =
  | "all"
  | "modules"
  | "courses"
  | "resources"
  | "check-ins";

type LibraryTypeNavItem = {
  value: LibraryTypeNavValue;
  label: string;
  Icon: AdminSecondaryNavItem["Icon"];
};

const LIBRARY_TYPE_NAV_ITEMS: LibraryTypeNavItem[] = [
  { value: "all", label: "All", Icon: GridOutlineIcon },
  { value: "modules", label: "Modules", Icon: BookOpenOutlineIcon },
  { value: "courses", label: "Courses", Icon: BookmarkOutlineIcon },
  { value: "resources", label: "Resources", Icon: FileTextOutlineIcon },
  {
    value: "check-ins",
    label: "Check-ins",
    Icon: CircleCheckmarkOutlineIcon,
  },
];

const getLibraryTypeHref = (
  value: LibraryTypeNavValue,
): React.ComponentProps<typeof Link>["href"] => {
  if (value === "all") return "/library";
  return { pathname: "/library", query: { type: value } };
};

const getActiveLibraryType = (
  type: string | string[] | undefined,
): LibraryTypeNavValue => {
  if (
    type === "modules" ||
    type === "courses" ||
    type === "resources" ||
    type === "check-ins"
  ) {
    return type;
  }
  return "all";
};

const LibraryTypeMenuItem: FC<{
  item: LibraryTypeNavItem;
  pathname: string;
  type: string | string[] | undefined;
}> = ({ item, pathname, type }) => {
  const Icon = item.Icon;
  const active =
    pathname === "/library" && getActiveLibraryType(type) === item.value;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        aria-current={active ? "page" : undefined}
        className={SECONDARY_NAV_MENU_BUTTON_CLASS}
      >
        <Link href={getLibraryTypeHref(item.value)}>
          <Icon />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const FolderMenuItem: FC<{
  folder: FolderLibraryItemFragment;
  pathname: string;
  activeId: string | string[] | undefined;
}> = ({ folder, pathname, activeId }) => {
  const active = pathname === "/library/folders/[id]" && activeId === folder.id;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        aria-current={active ? "page" : undefined}
        className={SECONDARY_NAV_MENU_BUTTON_CLASS}
      >
        <Link
          href={{
            pathname: "/library/folders/[id]",
            query: { id: folder.id },
          }}
        >
          <FolderOpenOutlineIcon />
          <span>{folder.name.en}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const getOperationsFormsItemActive = (
  href: string | undefined,
  pathname: string,
  queryType: string | string[] | undefined,
): boolean | undefined => {
  if (pathname === "/library/forms/alerts")
    return href === "/library/forms?type=task-lists";
  else if (pathname !== "/library/forms") return undefined;
  if (href === "/library/forms?type=audits") return queryType === "audits";
  if (href === "/library/forms?type=task-lists") {
    return queryType !== "audits";
  }
  return undefined;
};

const SecondaryMenuItem: FC<{
  item: AdminSecondaryNavItem;
  pathname: string;
  queryType?: string | string[];
}> = ({ item, pathname, queryType }) => {
  const Icon = item.Icon;
  const active =
    getOperationsFormsItemActive(item.href, pathname, queryType) ??
    item.isActive?.(pathname) ??
    pathname === item.href;

  if (item.disabled || !item.href) {
    return (
      <SidebarMenuItem className={item.sectionBreakBefore ? "mt-4" : undefined}>
        <SidebarMenuButton
          disabled
          className={cn(
            SECONDARY_NAV_MENU_BUTTON_CLASS,
            "cursor-not-allowed hover:bg-transparent",
          )}
        >
          <Icon />
          <span>{item.label}</span>
          <Text
            variant="micro-title"
            className="ml-auto rounded-full bg-gray-2 px-1.5 py-0.5 leading-none text-gray-8"
          >
            Soon
          </Text>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem className={item.sectionBreakBefore ? "mt-4" : undefined}>
      <SidebarMenuButton
        asChild
        isActive={active}
        aria-current={active ? "page" : undefined}
        className={SECONDARY_NAV_MENU_BUTTON_CLASS}
      >
        <Link href={item.href as React.ComponentProps<typeof Link>["href"]}>
          <Icon />
          <span>{item.label}</span>
          {item.TrailingIcon ? (
            <item.TrailingIcon className="ml-auto text-icon-disabled" />
          ) : null}
          {item.comingSoon ? (
            <Text
              variant="micro-title"
              className="ml-auto rounded-full bg-gray-2 px-1.5 py-0.5 leading-none text-gray-8"
            >
              Coming soon
            </Text>
          ) : null}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export default AdminSecondaryNavMenu;
